import type { Dispatch, SetStateAction } from 'react';
import type { ClaimRepairEntry } from './claimIntakeUtils';
import {
  makeEmptyLaborLineRow,
  makeEmptyPartLineRow,
  makeInitialClaimRepair,
  nextClaimIntakeLineId,
} from './claimIntakeUtils';
import { normalizeRepairOrderDateToMdy } from './claimIntakeUtils';
import type { ClaimIntakeFormEdits } from './mergeClaimObjectFormEdits';

/**
 * Returns the first stringifiable scalar from `value`. Arrays are flattened to their first element.
 * Used to gracefully handle parser fields that arrive as either a scalar or a single-item list.
 */
export function firstScalar(value: unknown): string {
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (
      typeof first === 'string' ||
      typeof first === 'number' ||
      typeof first === 'boolean' ||
      typeof first === 'bigint'
    ) {
      return String(first);
    }
    return '';
  }
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }
  return '';
}

/** VIN sits at the same level as `3Cs` in parser_results, under `Vehicle_Identification_Number`. */
export function extractVinFromParserResults(parserResults: Record<string, unknown> | null | undefined): string {
  if (!parserResults) return '';
  return firstScalar(parserResults.Vehicle_Identification_Number).trim();
}

/** Customer phone sits at the same level as `3Cs` in parser_results, under `customer_phone_number`. */
export function extractCustomerPhoneFromParserResults(
  parserResults: Record<string, unknown> | null | undefined
): string {
  if (!parserResults) return '';
  return firstScalar(parserResults.customer_phone_number).trim();
}

/** Servicer phone from estimateparser `invoice_generator_phone`, with legacy shop phone fallbacks. */
export function extractServicerPhoneFromParserResults(
  parserResults: Record<string, unknown> | null | undefined
): string {
  if (!parserResults) return '';
  return (
    firstScalar(parserResults.invoice_generator_phone).trim() ||
    firstScalar(parserResults.shop_phone).trim() ||
    firstScalar(parserResults.servicer_phone).trim()
  );
}

/** Contract number from estimateparser `contract_number`. */
export function extractContractNumberFromParserResults(
  parserResults: Record<string, unknown> | null | undefined
): string {
  if (!parserResults) return '';
  return firstScalar(parserResults.contract_number).trim();
}

/** Matches trailing `STATE ZIP` with optional ISO country (e.g. `TX 78009 US`). */
const STATE_ZIP_COUNTRY_SUFFIX =
  /\b([A-Z]{2})\s+(\d{5}(?:-\d{4})?)(?:\s+([A-Z]{2}))?\s*$/i;

/** Matches trailing ZIP with optional ISO country when state is absent. */
const TRAILING_ZIP_COUNTRY_SUFFIX = /\b(\d{5}(?:-\d{4})?)(?:\s+[A-Z]{2})?\s*$/;

/** Pulls a US ZIP from a single-line invoice address (e.g. "..., Bloomsburg, PA 17815"). */
export function extractPostalCodeFromParserAddress(addr: unknown): string {
  const line = firstScalar(addr).trim();
  if (!line) return '';
  const stateZip = line.match(STATE_ZIP_COUNTRY_SUFFIX);
  if (stateZip) return stateZip[2];
  const trailingZip = line.match(TRAILING_ZIP_COUNTRY_SUFFIX);
  return trailingZip ? trailingZip[1] : '';
}

export type ParsedParserAddress = {
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
};

const EMPTY_PARSER_ADDRESS: ParsedParserAddress = {
  address1: '',
  address2: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode: '',
};

function pickAddressField(obj: unknown, ...fieldNames: string[]): string {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return '';
  const record = obj as Record<string, unknown>;
  for (const name of fieldNames) {
    const v = firstScalar(record[name]).trim();
    if (v) return v;
  }
  return '';
}

function getFirstParserAddressEntry(raw: unknown): unknown {
  if (Array.isArray(raw)) return raw[0] ?? null;
  return raw ?? null;
}

/** Parses the city / state / postal segment (e.g. `"GUILFORD, NY 13780"` or `"Castroville, TX 78009 US"`). */
function parseCityStatePostalSegment(
  segment: string
): Pick<ParsedParserAddress, 'city' | 'state' | 'postalCode' | 'countryCode'> {
  const trimmed = segment.trim();
  if (!trimmed) return { city: '', state: '', postalCode: '', countryCode: '' };

  const postalCode = extractPostalCodeFromParserAddress(trimmed);
  const stateZip = STATE_ZIP_COUNTRY_SUFFIX.exec(trimmed);
  if (!stateZip) {
    return { city: trimmed.replace(/,+$/, '').trim(), state: '', postalCode, countryCode: '' };
  }

  const state = stateZip[1].toUpperCase();
  const countryCode = stateZip[3]?.toUpperCase() ?? '';
  const beforeStateZip = trimmed.slice(0, stateZip.index).trim().replace(/,\s*$/, '');
  const commaParts = beforeStateZip.split(',').map((p) => p.trim()).filter(Boolean);
  const city = commaParts.length > 0 ? commaParts[commaParts.length - 1] : beforeStateZip;

  return { city, state, postalCode, countryCode };
}

/** Splits street vs city from the segment before `, STATE ZIP` (e.g. `"505 W 28TH ST HIALEAH"`). */
function splitStreetAndCityBeforeState(beforeStateZip: string): Pick<ParsedParserAddress, 'address1' | 'city'> {
  const trimmed = beforeStateZip.trim();
  if (!trimmed) return { address1: '', city: '' };

  const lastComma = trimmed.lastIndexOf(',');
  if (lastComma >= 0) {
    return {
      address1: trimmed.slice(0, lastComma).trim(),
      city: trimmed.slice(lastComma + 1).trim(),
    };
  }

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return {
      address1: words.slice(0, -1).join(' '),
      city: words[words.length - 1],
    };
  }

  return { address1: '', city: trimmed };
}

/** Splits a US address string into street / city / state / postal code. */
function splitSingleLineParserAddress(line: string): ParsedParserAddress {
  const trimmed = line.trim();
  if (!trimmed) return { ...EMPTY_PARSER_ADDRESS };

  const lines = trimmed.split(/\r?\n/).map((p) => p.trim()).filter(Boolean);
  if (lines.length >= 2) {
    const locality = parseCityStatePostalSegment(lines.slice(1).join(', '));
    return {
      ...EMPTY_PARSER_ADDRESS,
      address1: lines[0],
      city: locality.city,
      state: locality.state,
      postalCode: locality.postalCode,
      countryCode: locality.countryCode,
    };
  }

  const postalCode = extractPostalCodeFromParserAddress(trimmed);
  const stateZip = STATE_ZIP_COUNTRY_SUFFIX.exec(trimmed);
  if (!stateZip) {
    return { ...EMPTY_PARSER_ADDRESS, address1: trimmed, postalCode };
  }

  const state = stateZip[1].toUpperCase();
  const countryCode = stateZip[3]?.toUpperCase() ?? '';
  const beforeStateZip = trimmed.slice(0, stateZip.index).trim().replace(/,\s*$/, '');
  const { address1, city } = splitStreetAndCityBeforeState(beforeStateZip);
  return { ...EMPTY_PARSER_ADDRESS, address1, city, state, postalCode, countryCode };
}

function mergeObjectParserAddressFields(entry: Record<string, unknown>): ParsedParserAddress {
  const address1Raw = pickAddressField(entry, 'address1');
  const address2 = pickAddressField(entry, 'address2');
  const cityRaw = pickAddressField(entry, 'city');
  const stateRaw = pickAddressField(entry, 'state', 'state_code', 'State');
  const postalRaw = pickAddressField(entry, 'postalCode', 'postalcode', 'postal_code', 'zip');
  const countryCode = pickAddressField(entry, 'countryCode', 'countrycode', 'country_code', 'country');

  const splitFromLine = address1Raw ? splitSingleLineParserAddress(address1Raw) : { ...EMPTY_PARSER_ADDRESS };
  const splitHasParts = Boolean(splitFromLine.city || splitFromLine.state || splitFromLine.postalCode);

  return {
    address1: splitHasParts ? splitFromLine.address1 : address1Raw,
    address2,
    city: cityRaw || splitFromLine.city,
    state: stateRaw || splitFromLine.state,
    postalCode: postalRaw || splitFromLine.postalCode,
    countryCode,
  };
}

/**
 * Parses estimateparser `customer_address` / `invoice_generator_address`.
 * Structured objects use `address1` for street only; city, state, and postal code are separate fields.
 * Legacy single-line strings are split when possible so city/state/postal are not duplicated in address1.
 */
export function parseParserStructuredAddress(raw: unknown): ParsedParserAddress {
  const entry = getFirstParserAddressEntry(raw);
  if (entry == null) return { ...EMPTY_PARSER_ADDRESS };

  if (typeof entry === 'string') {
    return splitSingleLineParserAddress(entry);
  }

  if (typeof entry === 'object' && !Array.isArray(entry)) {
    return mergeObjectParserAddressFields(entry as Record<string, unknown>);
  }

  return { ...EMPTY_PARSER_ADDRESS };
}

export function applyParsedParserAddressFromRaw(
  raw: unknown,
  setters: ParserAddressSetters
): void {
  applyParserAddressToSetters(parseParserStructuredAddress(raw), setters);
}

export type ParserAddressSetters = Readonly<{
  onAddress1Change?: (v: string) => void;
  onAddress2Change?: (v: string) => void;
  onCityChange?: (v: string) => void;
  onStateChange?: (v: string) => void;
  onPostalCodeChange?: (v: string) => void;
  onCountryCodeChange?: (v: string) => void;
}>;

function applyParserAddressToSetters(parsed: ParsedParserAddress, setters: ParserAddressSetters): void {
  const apply = (setter: ((v: string) => void) | undefined, value: string) => {
    if (value && setter) setter(value);
  };
  apply(setters.onAddress1Change, parsed.address1);
  apply(setters.onAddress2Change, parsed.address2);
  apply(setters.onCityChange, parsed.city);
  apply(setters.onStateChange, parsed.state);
  apply(setters.onPostalCodeChange, parsed.postalCode);
  apply(setters.onCountryCodeChange, parsed.countryCode);
}

export type ParserResultsFormSetters = Readonly<{
  onServicerNameChange: (v: string) => void;
  onServicerPhoneChange?: (v: string) => void;
  onServicerAddress1Change?: (v: string) => void;
  onServicerAddress2Change?: (v: string) => void;
  onServicerCityChange?: (v: string) => void;
  onServicerStateChange?: (v: string) => void;
  /** Populates servicer postal code from the parsed PDF invoice address. */
  onServicerPostalCodeChange?: (v: string) => void;
  onServicerCountryCodeChange?: (v: string) => void;
  onLaborRateChange: (v: string) => void;
  onContactEmailChange: (v: string) => void;
  onRepairsChange: Dispatch<SetStateAction<ClaimRepairEntry[]>>;
}>;

export type ParserInvoiceMetaSetters = Readonly<{
  onMileageInChange: (v: string) => void;
  onInvoiceNumberChange: (v: string) => void;
  onRepairOrderDateChange: (v: string) => void;
  onCustomerNameChange: (v: string) => void;
  onCustomerPhoneChange: (v: string) => void;
  onCustomerAddress1Change?: (v: string) => void;
  onCustomerAddress2Change?: (v: string) => void;
  onCustomerCityChange?: (v: string) => void;
  onCustomerStateChange?: (v: string) => void;
  onCustomerPostalCodeChange?: (v: string) => void;
  onCustomerCountryCodeChange?: (v: string) => void;
  onVehicleVinForClaimChange: (v: string) => void;
  onContractNumberChange?: (v: string) => void;
}>;

export type ClaimIntakeParserSetters = ParserResultsFormSetters & ParserInvoiceMetaSetters;

/**
 * Maps estimateparser `parser_results['3Cs']` into repair rows.
 * Each 3C entry includes nested `part_lines` and `labor_lines` for that repair.
 */
export function buildClaimRepairEntriesFromParser(parserResults: Record<string, unknown>): ClaimRepairEntry[] | null {
  const threeCsRaw = parserResults['3Cs'];
  if (!Array.isArray(threeCsRaw) || threeCsRaw.length === 0) return null;

  const out: ClaimRepairEntry[] = [];

  for (let i = 0; i < threeCsRaw.length; i++) {
    const threeC = threeCsRaw[i];
    if (threeC == null || typeof threeC !== 'object' || Array.isArray(threeC)) continue;
    const row = threeC as Record<string, unknown>;

    const partsRaw = Array.isArray(row.part_lines) ? row.part_lines : [];
    const laborRaw = Array.isArray(row.labor_lines) ? row.labor_lines : [];

    const part_lines: ClaimRepairEntry['part_lines'] = (partsRaw as Record<string, unknown>[]).map((p) => ({
      id: nextClaimIntakeLineId(),
      part_number: firstScalar(p.part_number),
      part_description: firstScalar(p.part_description),
      part_quantity: firstScalar(p.part_quantity),
      part_unit_price: firstScalar(p.part_unit_price),
      part_amount: firstScalar(p.part_amount),
    }));
    if (part_lines.length === 0) part_lines.push(makeEmptyPartLineRow());

    const labor_lines: ClaimRepairEntry['labor_lines'] = (laborRaw as Record<string, unknown>[]).map((l) => ({
      id: nextClaimIntakeLineId(),
      labor_description: firstScalar(l.labor_description),
      labor_amount: firstScalar(l.labor_amount),
      labor_hours: firstScalar(l.labor_hours ?? ''),
      labor_op_code: firstScalar(l.labor_repair_notes ?? l.labor_op_code ?? ''),
      allowFixedValue: false,
    }));
    if (labor_lines.length === 0) labor_lines.push(makeEmptyLaborLineRow());

    out.push({
      id: i + 1,
      complaint: firstScalar(row.complaint),
      cause: firstScalar(row.cause),
      correction: firstScalar(row.correction),
      tiretread: firstScalar(row.tiretread),
      labor_lines,
      part_lines,
    });
  }

  return out.length > 0 ? out : null;
}

/** Reads `parser_results` stored on the parsed estimate document row. */
export function readParserResultsFromDocument(
  parsedClaimDocument: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  const raw = parsedClaimDocument?.parser_results;
  return raw != null && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null;
}

/**
 * Maps estimateparser `parser_results` into the same shape used for claim `form_state` merges.
 * Used by servicer_info so parsed PDF data is always included in the API payload.
 */
export function buildClaimFormEditsFromParserResults(
  parserResults: Record<string, unknown>,
  fallbackRepairs: ClaimRepairEntry[] = [makeInitialClaimRepair(1)]
): ClaimIntakeFormEdits {
  const getName = (arr: unknown) => (Array.isArray(arr) ? arr[0] ?? '' : '');

  const servicerAddr = parseParserStructuredAddress(parserResults.invoice_generator_address);
  const built = buildClaimRepairEntriesFromParser(parserResults);

  const rate =
    firstScalar(parserResults.labor_rate) ||
    firstScalar(parserResults.hourly_labor_rate) ||
    firstScalar(parserResults.shop_labor_rate);

  const servicerPhone = extractServicerPhoneFromParserResults(parserResults);

  const mileage =
    firstScalar(parserResults.MILEGE_IN) ||
    firstScalar(parserResults.MILEAGE_IN) ||
    firstScalar(parserResults.mileage_in);

  const inv = firstScalar(parserResults.Invoice_Number) || firstScalar(parserResults.invoice_number);

  const roDate = normalizeRepairOrderDateToMdy(
    firstScalar(parserResults.repair_order_date) ||
      firstScalar(parserResults.repair_order_date_ymd)
  );

  return {
    repairs: built && built.length > 0 ? built : fallbackRepairs,
    laborRate: rate,
    mileageIn: mileage,
    invoiceNumber: inv,
    repairOrderDate: roDate,
    servicerName: String(getName(parserResults.invoice_generator_name)),
    servicerPhone,
    servicerAddress: {
      address1: servicerAddr.address1,
      address2: servicerAddr.address2,
      city: servicerAddr.city,
      state: servicerAddr.state,
      postalCode: servicerAddr.postalCode,
      countryCode: servicerAddr.countryCode,
    },
  };
}

/**
 * Pushes recognised estimateparser fields into the matching step-3 setter.
 * Customer fields are intentionally excluded — they are populated from the VIN contract lookup only.
 * Contact email is manual entry only (not mapped from parser_results).
 */
export function applyParserResultsToFormState(
  parserResults: Record<string, unknown>,
  setters: ClaimIntakeParserSetters
): void {
  const getName = (arr: unknown) => (Array.isArray(arr) ? arr[0] ?? '' : '');

  if (getName(parserResults.invoice_generator_name)) {
    setters.onServicerNameChange(String(getName(parserResults.invoice_generator_name)));
  }

  const servicerPhone = extractServicerPhoneFromParserResults(parserResults);
  if (servicerPhone) setters.onServicerPhoneChange?.(servicerPhone);

  applyParserAddressToSetters(parseParserStructuredAddress(parserResults.invoice_generator_address), {
    onAddress1Change: setters.onServicerAddress1Change,
    onAddress2Change: setters.onServicerAddress2Change,
    onCityChange: setters.onServicerCityChange,
    onStateChange: setters.onServicerStateChange,
    onPostalCodeChange: setters.onServicerPostalCodeChange,
    onCountryCodeChange: setters.onServicerCountryCodeChange,
  });

  const rate =
    firstScalar(parserResults.labor_rate) ||
    firstScalar(parserResults.hourly_labor_rate) ||
    firstScalar(parserResults.shop_labor_rate);
  if (rate) setters.onLaborRateChange(rate);

  const mileage =
    firstScalar(parserResults.MILEGE_IN) ||
    firstScalar(parserResults.MILEAGE_IN) ||
    firstScalar(parserResults.mileage_in);
  if (mileage) setters.onMileageInChange(mileage);

  const inv = firstScalar(parserResults.Invoice_Number) || firstScalar(parserResults.invoice_number);
  if (inv) setters.onInvoiceNumberChange(inv);

  const roDate = normalizeRepairOrderDateToMdy(
    firstScalar(parserResults.repair_order_date) ||
      firstScalar(parserResults.repair_order_date_ymd)
  );
  if (roDate) setters.onRepairOrderDateChange(roDate);

  const vin = extractVinFromParserResults(parserResults);
  if (vin) setters.onVehicleVinForClaimChange(vin);

  const contractNumber = extractContractNumberFromParserResults(parserResults);
  if (contractNumber) setters.onContractNumberChange?.(contractNumber);

  const built = buildClaimRepairEntriesFromParser(parserResults);
  if (built && built.length > 0) {
    setters.onRepairsChange(built);
  }
}
